import { formatDate } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { ControlsOf, FormArray, FormControl, FormGroup } from '@ngneat/reactive-forms';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { AanAfwezigheid } from 'src/app/afas-classes/aanafwezigheid.class';
import { Realisation, RealisationHourtype } from 'src/app/classes/realisation';
import { User } from 'src/app/classes/user.class';
import { CodaltComponent } from 'src/app/codalt.component';
import { AfasService } from 'src/app/services/afas.service';
import { AuthorisationService } from 'src/app/services/auth/authorisation.service';
import { Functionalities } from 'src/app/services/auth/functionalities';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog-service/confirm-dialog.service';
import { RealisationService } from 'src/app/services/realisation.service';
import { TimePickerService } from 'src/app/services/time-picker.service';
import { Settings } from 'src/app/settings.class';
import { LocalStorage } from 'src/app/storage.class';
import { Utils } from 'src/app/utils.class';
import { Features } from '../../services/auth/features';
import {
    AddRealisationDialogComponent,
    NON_CONFLICTING_HOURTYPES,
} from '../check-hours/add-realisation-dialog/add-realisation-dialog.component';
import { EditRealisationProjectDialogComponent } from '../check-hours/edit-realisation-project-dialog/edit-realisation-project-dialog.component';
import { CommentDialogComponent } from './comment-dialog/comment-dialog.component';

@Component({
    selector: 'app-hour-registration',
    templateUrl: './hour-registration.component.html',
    styleUrls: ['./hour-registration.component.scss'],
    standalone: false,
})
export class HourRegistrationComponent extends CodaltComponent implements OnInit {
    RealisationHourtype = RealisationHourtype;

    maxDate: Date;
    date: Date;
    user: User;
    realisations: Realisation[];
    entityRealisations: Realisation[];

    pauseToday: number;
    minutesToday: number;
    pauseThisWeek: number;
    minutesThisWeek: number;

    afasAanAfwezigheid: AanAfwezigheid[];

    formPause: FormGroup<
        ControlsOf<{
            pause: number;
            changed: boolean;
        }>
    > = new FormGroup({
        changed: new FormControl(false),
        pause: new FormControl(),
    });
    form = new FormArray<ControlsOf<fgRealisation>>([]);
    formMap: Map<Realisation, FormGroup<ControlsOf<fgRealisation>>>;
    entityRealisationsMap: Map<Realisation, Realisation[]>;

    approved = false;
    submitted = false;
    saving = false;
    firstPendingDate: string | null = null;
    lastReload = new Date().getTime();
    daySubscriptions = new Subscription();
    addAllowed = false;
    approvedCommentAllowed = false;
    overlapping = false;
    overlappingTimeout: any;

    backTo = '/weekschedule';

    constructor(
        private dialog: MatDialog,
        private timePickerService: TimePickerService,
        private activatedRoute: ActivatedRoute,
        private afasService: AfasService,
        private router: Router,
        private confirmDialog: ConfirmDialogService,
        private confirmDialogService: ConfirmDialogService,
        private realisationService: RealisationService
    ) {
        super();
        this.maxDate = Utils.setTime(new Date(), 0, 0);
    }

    ngOnInit(): void {
        this.subscriptions.add(
            this.activatedRoute.params.subscribe((params: { date: string; userId: number; backTo: 'verrekening' }) => {
                const today = new Date();
                this.date = params.date ? new Date(params.date) : new Date(formatDate(today, 'yyyy-MM-dd', 'nl'));
                this.getData(params.userId);
                window.scroll(0, 0);
                if (params.backTo) {
                    this.backTo = params.backTo;
                }
            })
        );

        window.onfocus = () => {
            if (new Date().getTime() - this.lastReload > 20000 && !this.form.dirty && !this.formPause.dirty) {
                this.lastReload = new Date().getTime();
                this.getData(this.user?.id);
            }
        };
    }

    add() {
        const ref = this.dialog.open(AddRealisationDialogComponent, {
            maxWidth: '550px',
            width: '100vw',
            maxHeight: '100%',
            disableClose: false,
            panelClass: 'comment-edit-dialog',
            data: {
                realisations: this.realisations,
                bookdate: this.date,
                userId: this.user?.id ?? LocalStorage.user?.id,
            },
        });
        this.daySubscriptions.add(
            ref.afterClosed().subscribe(() => {
                this.getData(this.user?.id);
            })
        );
    }

    openChangesBackActionCheck(): Promise<boolean> {
        return new Promise(resolve => {
            if (this.form.dirty) {
                this.confirmDialog
                    .confirm({
                        title: 'Niet opgeslagen wijzigingen',
                        text: `Wilt u de niet opgeslagen wijzigingen verwerpen?`,
                        confirm: 'Hier blijven',
                        cancel: 'Wijzigingen verwerpen',
                    })
                    .then(
                        () => {
                            resolve(false);
                        },
                        () => {
                            resolve(true);
                        }
                    );
            } else {
                resolve(true);
            }
        });
    }

    delete(realisation: Realisation) {
        this.confirmDialogService
            .confirm({
                title: 'Registratie verwijderen',
                text: `Wil je de uren ${formatDate(realisation.begindate, 'EEE HH:mm', 'nl')}-${formatDate(realisation.enddate, 'EEE HH:mm', 'nl')} ${Utils.realisationAfasProjectId(realisation) ?? realisation.leave?.comment} verwijderen?`,
                confirm: 'Verwijderen',
                cancel: 'Behouden',
            })
            .then(
                () => {
                    this.formMap.get(realisation).controls.removed.setValue(new Date());
                },
                () => {}
            );
    }

    restore(realisation: Realisation) {
        this.formMap.get(realisation).controls.removed.setValue(null);
    }

    private getData(userId?: number) {
        this.daySubscriptions?.unsubscribe();
        this.daySubscriptions = new Subscription();

        const now = new Date();
        now.setUTCHours(-3);
        const diff = (now.getTime() - this.date.getTime()) / 1000 / 60 / 60;
        this.addAllowed = diff < Settings.HOUR_EDIT_ALLOWED_HOURS_EXTENDED;

        // approved comment is allowed for the whole week untill next sunday
        const previousMonday = new Date();
        Utils.setTime(previousMonday, 0, 0);
        previousMonday.setDate(previousMonday.getDate() - ((previousMonday.getDay() + 6) % 7) - 7);
        this.approvedCommentAllowed = now.getTime() > previousMonday.getTime();

        this.afasAanAfwezigheid = [];

        if (AuthorisationService.hasFeature(Features.RealisationScheduleFirstUnsubmitted)) {
            this.daySubscriptions.add(
                this.realisationService.getFirstUnsubmittedDay(this.date, userId).subscribe(r => {
                    this.firstPendingDate = r.data;
                })
            );
        }
        if (!this.environment.disableAfas) {
            this.daySubscriptions.add(
                this.afasService.getOwnAanAfwezigheid(this.date, this.date, userId).subscribe(aanAfw => {
                    this.afasAanAfwezigheid = aanAfw.data;
                })
            );
        }

        this.daySubscriptions.add(
            this.realisationService.getUserDaySchedule(this.date, userId).subscribe(userDaySchedule => {
                setTimeout(() => {
                    this.saving = false;
                }, 300);
                this.form = new FormArray<ControlsOf<fgRealisation>>([]);
                const realisations = userDaySchedule.data.realisations.filter(r => !!r.user_id);
                this.entityRealisations = userDaySchedule.data.realisations.filter(r => !!r.entity_id);
                const formMap = new Map<Realisation, FormGroup<ControlsOf<fgRealisation>>>();
                const entityRealisationsMap = new Map<Realisation, Realisation[]>();

                this.user = userDaySchedule.data?.user;
                realisations.forEach(realisation => {
                    const formgroup = new FormGroup<ControlsOf<fgRealisation>>({
                        id: new FormControl(realisation.id),
                        begindate: new FormControl(new Date(realisation.begindate)),
                        enddate: new FormControl(new Date(realisation.enddate)),
                        planning_id: new FormControl(realisation.planning_id),
                        updated_at: new FormControl(realisation.updated_at),
                        comment_user: new FormControl(realisation.comment_user),
                        comment_user_approved: new FormControl(realisation.comment_user_approved),
                        removed: new FormControl(realisation.removed),
                    });
                    this.daySubscriptions.add(
                        formgroup.valueChanges.pipe(debounceTime(250)).subscribe(values => {
                            this.realisationService.saveRealisation(values as any).subscribe(
                                result => {
                                    result.data.realisation.begindate = new Date(result.data.realisation.begindate);
                                    result.data.realisation.enddate = new Date(result.data.realisation.enddate);
                                    Object.assign(realisation, result.data.realisation);
                                    formgroup.patchValue(result.data.realisation, { emitEvent: false, onlySelf: true });
                                    this.getTotals(userId);
                                    this.form.markAsPristine();
                                },
                                () => {
                                    this.getData(userId);
                                }
                            );
                        })
                    );
                    this.form.push(formgroup);
                    formMap.set(realisation, formgroup);
                    entityRealisationsMap.set(
                        realisation,
                        this.entityRealisations
                            .filter(
                                er =>
                                    er.planning_has_entity_id ===
                                        (realisation.planning_has_entity_id ?? 'donotmapnull') ||
                                    er.parent_realisation_id === realisation.id
                            )
                            .filter(er => er.hourtype === RealisationHourtype.worktime)
                    );
                });
                this.formMap = formMap;
                this.entityRealisationsMap = entityRealisationsMap;
                this.realisations = realisations.sort((a, b) => Utils.realisationSort(a) - Utils.realisationSort(b));

                this.getTotals(userId);
            })
        );
    }

    private getTotals(userId?: number) {
        this.daySubscriptions.add(
            this.realisationService.getUserDayTotals(this.date, userId).subscribe(data => {
                const hasRealisations = this.realisations.length > 0;

                this.approved = hasRealisations && this.realisations.every(realisation => realisation.approved);
                this.submitted = hasRealisations && this.realisations.every(realisation => realisation.submitted);

                this.pauseThisWeek = data.weekTotal?.pause ?? 0;
                this.minutesThisWeek = (data.weekTotal?.minutes ?? 0) - this.pauseThisWeek;
                this.pauseToday = data.dayTotal?.pause ?? 0;
                this.minutesToday = (data.dayTotal?.minutes ?? 0) - this.pauseToday;
                this.formPause.controls.pause.setValue(this.pauseToday);

                if (this.overlappingTimeout) {
                    clearTimeout(this.overlappingTimeout);
                }
                this.overlappingTimeout = setTimeout(() => this.checkOverlapping(), 100);
            })
        );
    }

    private checkOverlapping() {
        let overlapping = false;
        const nonRemovedRealisations = this.realisations.filter(
            r => !r.removed && !NON_CONFLICTING_HOURTYPES.includes(r.hourtype)
        );
        nonRemovedRealisations.forEach((r, i) => {
            if (i > 0 && Utils.newDate(r.begindate) < Utils.newDate(nonRemovedRealisations[i - 1]?.enddate)) {
                overlapping = true;
            }
        });
        this.overlapping = overlapping;
    }

    editComment(fcComment: FormControl<string>, title?: string) {
        const ref = this.dialog.open(CommentDialogComponent, {
            maxWidth: '500px',
            maxHeight: '100%',
            disableClose: false,
            panelClass: 'comment-edit-dialog',
            data: {
                comment: fcComment.value,
                title,
            },
        });
        ref.afterClosed().subscribe(comment => {
            if (comment !== undefined) {
                fcComment.setValue(comment);
            }
        });
    }

    editTime(realisation: Realisation, field: 'begindate' | 'enddate', addToCurrentTime = 0) {
        const formGroup = this.formMap.get(realisation);
        if (this.approved) {
            return;
        }
        const firstRealisation =
            realisation.user_id &&
            this.realisations
                .filter(r => !r.removed)
                .sort((a, b) => Utils.getTimeOrNull(a.begindate) - Utils.getTimeOrNull(b.begindate))[0].id ===
                realisation.id;

        this.daySubscriptions.add(
            this.timePickerService
                .editRealisationTime(realisation, field, formGroup, addToCurrentTime, firstRealisation)
                .subscribe(newDate => {
                    if (newDate) {
                        this.processTimeChange(realisation, newDate, formGroup, field);
                        if (field === 'begindate' && newDate.getTime() > Utils.getTimeOrNull(realisation.enddate)) {
                            const endDate = new Date(newDate);
                            do {
                                endDate.setMinutes(endDate.getMinutes() + 1);
                            } while (endDate.getMinutes() % 3 !== 0);
                            this.processTimeChange(realisation, endDate, formGroup, 'enddate');
                        }
                    }
                })
        );
    }

    private processTimeChange(
        realisation: Realisation,
        newDate,
        formGroup: FormGroup<ControlsOf<fgRealisation>>,
        field: 'begindate' | 'enddate'
    ) {
        const fromIndex = this.realisations.indexOf(realisation);
        const diff = Utils.getTimeOrNull(newDate) - Utils.getTimeOrNull(formGroup.get(field).value);
        if (realisation.hourtype === RealisationHourtype.travel_to) {
            // Only transfer Futures until the worktime
            const toIndex = this.realisations.findIndex(r => r.hourtype === RealisationHourtype.worktime);
            const items = this.realisations.slice(fromIndex, toIndex + 1);
            items.forEach((r, index) => {
                if (index !== 0 || field === 'begindate') {
                    // When item is not the first one, or we are editing the begintime, update the begintime
                    const timeToSet = new Date(this.formMap.get(r).get('begindate').value);
                    timeToSet.setTime(timeToSet.getTime() + diff);
                    this.formMap.get(r).get('begindate').setValue(timeToSet);
                    this.formMap.get(r).markAsDirty();
                }
                if (index !== items.length - 1) {
                    // When item is not the last one, extend with the diff time from changed realisation
                    const timeToSet = new Date(this.formMap.get(r).get('enddate').value);
                    timeToSet.setTime(timeToSet.getTime() + diff);
                    this.formMap.get(r).get('enddate').setValue(timeToSet);
                    this.formMap.get(r).markAsDirty();
                } else {
                    // When item IS the last one, make sure it lasts at least 1 hour
                    const start = new Date(this.formMap.get(r).get('begindate').value);
                    const end = new Date(this.formMap.get(r).get('enddate').value);
                    const diff = end.getTime() - start.getTime();
                    const oneHour = 3600000;
                    if (diff < oneHour) {
                        const toAdd = oneHour - diff;
                        this.transferAllFutureTimes(r, toAdd);
                    }
                }
            });
        } else if (field === 'enddate') {
            this.transferAllFutureTimes(realisation, diff);
        } else {
            formGroup.get(field).setValue(newDate);
            formGroup.markAsDirty();
        }
    }

    transferAllFutureTimes(fromRealisation: Realisation, timeInSeconds) {
        const fromIndex = this.realisations.indexOf(fromRealisation);
        const to = this.realisations.slice(fromIndex).find((r, index, realisations) => {
            return (
                index < realisations.length - 1 &&
                new Date(r.enddate).getTime() !== new Date(realisations[index + 1].begindate).getTime()
            );
        });
        const toIndex = to ? this.realisations.indexOf(to) : this.realisations.length - 1;
        this.realisations.slice(fromIndex, toIndex + 1).forEach((r, index) => {
            ['begindate', 'enddate'].forEach((field: 'begindate' | 'enddate') => {
                if (index !== 0 || field === 'enddate') {
                    const timeToSet = new Date(this.formMap.get(r).get(field).value);
                    timeToSet.setTime(timeToSet.getTime() + timeInSeconds);
                    this.formMap.get(r).get(field).setValue(timeToSet);
                    this.formMap.get(r).markAsDirty();
                }
            });
        });
    }

    submitHours() {
        this.saving = true;
        this.realisationService.submitRealisation(this.realisations).subscribe(
            () => {
                this.getData(this.user?.id);
            },
            error => {
                this.confirmDialog
                    .confirm({
                        title: 'Fout',
                        text: `Er ging iets fout bij het opslaan<br>${error.message}`,
                        confirm: 'Sluiten',
                        cancel: null,
                    })
                    .then(() => {
                        this.getData(this.user?.id);
                    });
            }
        );
    }

    next() {
        if (this.date < this.maxDate) {
            this.date.setDate(this.date.getDate() + 1);
            const userId = this.user?.id ? `/${this.user.id}` : '';
            this.router.navigate([
                `/hours/${formatDate(this.date, 'yyyy-MM-dd', 'nl')}/${userId}`,
                { backTo: this.backTo },
            ]);
        }
    }

    prev() {
        const userId = this.user?.id ? `${this.user.id}` : '';
        if (this.firstPendingDate) {
            const firstOpenDay = this.firstPendingDate;
            this.router.navigate([
                `/hours/${formatDate(firstOpenDay, 'yyyy-MM-dd', 'nl')}/${userId}`,
                { backTo: this.backTo },
            ]);
        } else {
            this.date.setDate(this.date.getDate() - 1);
            this.router.navigate([
                `/hours/${formatDate(this.date, 'yyyy-MM-dd', 'nl')}/${userId}`,
                { backTo: this.backTo },
            ]);
        }
    }

    today() {
        this.date = new Date(formatDate(new Date(), 'yyyy-MM-dd', 'nl'));
        const userId = this.user?.id ? `/${this.user.id}` : '';
        this.router.navigateByUrl(`hours/${formatDate(this.date, 'yyyy-MM-dd', 'nl')}${userId}`);
    }

    changePause() {
        const options = [
            {
                minutes: 60,
                name: '1 uur',
            },
            {
                minutes: 45,
                name: '3 kwartier',
            },
            {
                minutes: 30,
                name: 'Half uur',
            },
            {
                minutes: 15,
                name: 'Kwartier',
            },
            {
                minutes: 0,
                name: 'Geen',
            },
        ];
        this.confirmDialogService
            .confirm({
                title: 'Pauzeduur aanpassen',
                text: '',
                confirm: 'Annuleren',
                cancel: null,
                options,
            })
            .then(
                (data: { minutes: number }) => {
                    if (data) {
                        this.formPause.controls.pause.setValue(data.minutes);
                        this.formPause.controls.changed.setValue(true);
                        this.realisationService
                            .changePause(this.date, this.formPause.controls.pause.value, this.user?.id)
                            .subscribe(() => {
                                this.getTotals(this.user?.id);
                                this.formPause.markAsPristine();
                            });
                    }
                },
                () => {}
            );
    }

    editProject(realisation: Realisation) {
        if (!AuthorisationService.hasFunctionality(Functionalities.CommonHourCheck)) {
            const now = new Date();
            const enddate = new Date(realisation.enddate);

            const diff = (now.getTime() - enddate.getTime()) / 1000 / 60 / 60;
            if (diff >= Settings.HOUR_EDIT_ALLOWED_HOURS || realisation.approved) {
                return;
            }
        }
        if (realisation.leave || realisation.paid) {
            return;
        }

        const ref = this.dialog.open(EditRealisationProjectDialogComponent, {
            maxWidth: '450px',
            width: '100vw',
            maxHeight: '100%',
            disableClose: false,
            panelClass: 'comment-edit-dialog',
            data: {
                realisations: this.realisations,
                realisation,
                bookdate: this.date,
            },
        });
        this.daySubscriptions.add(
            ref.afterClosed().subscribe(rel => {
                if (rel) {
                    this.getData(realisation.user_id);
                }
            })
        );
    }
}

interface fgRealisation {
    id: number;
    begindate: FormControl<Date>;
    enddate: FormControl<Date>;
    planning_id: number;
    updated_at: FormControl<Date>;
    comment_user: string;
    comment_user_approved: string;
    removed: FormControl<Date>;
}
